import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, X, Send, Headset, Loader2, Bot } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, streamSSE } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../ui/input";

export function Chatbot() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState({ enabled: true, greeting: "" });
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [hf, setHf] = useState({ name: "", email: "", message: "" });
  const sid = useRef(localStorage.getItem("rg2k_ai_sid") || "");
  const endRef = useRef(null);

  useEffect(() => {
    api.get("/chatbot-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, handoff]);

  const send = async (e) => {
    e?.preventDefault();
    const q = text.trim();
    if (!q || busy) return;
    setText("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    try {
      const fin = await streamSSE("/ai/chat", { session_id: sid.current, message: q }, (d) =>
        setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: m[m.length - 1].text + d }])
      );
      if (fin.session_id) {
        sid.current = fin.session_id;
        localStorage.setItem("rg2k_ai_sid", fin.session_id);
      }
    } catch {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", text: "Sorry, something went wrong. Try Talk to Support." }]);
    }
    setBusy(false);
  };

  const talkToSupport = async (e) => {
    e?.preventDefault();
    try {
      if (user && user.role !== "admin") {
        await api.post("/ai/handoff", { session_id: sid.current, message: hf.message });
        toast.success("Connected to support");
        setOpen(false);
        nav("/dashboard/messages");
        return;
      }
      await api.post("/ai/handoff", { session_id: sid.current, ...hf });
      setHandoff(false);
      setMsgs((m) => [...m, { role: "assistant", text: "Thanks! Our support team received your request and will reply by email soon." }]);
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  if (!cfg.enabled) return null;
  return (
    <>
      <button onClick={() => setOpen(!open)} className="fixed bottom-5 right-5 z-50 icon-ring w-14 h-14 bg-[#007bff] text-white" data-testid="chatbot-toggle-btn" aria-label="Open chat">
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
      {open && (
        <div className="fixed z-50 bottom-24 right-3 left-3 sm:left-auto sm:right-5 sm:w-[380px] h-[70vh] max-h-[560px] card-neon flex flex-col overflow-hidden fade-up" data-testid="chatbot-panel">
          <div className="px-4 py-3 border-b border-[#007bff]/30 flex items-center gap-3 bg-[#061232]">
            <span className="icon-ring w-9 h-9"><Bot className="w-5 h-5" /></span>
            <div className="flex-1"><p className="font-cond font-bold uppercase text-white leading-none">RG2K Assistant</p><p className="text-xs text-slate-400">AI answers · humans on request</p></div>
          </div>
          <div className="flex-1 overflow-y-auto scroll-thin p-4 space-y-3 text-sm">
            <div className="bg-[#0b1a3d] rounded-xl rounded-tl-sm p-3 text-slate-200 max-w-[85%]">{cfg.greeting}</div>
            {msgs.map((m, i) => (
              <div key={i} className={`p-3 rounded-xl max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-[#007bff] text-white rounded-tr-sm" : "bg-[#0b1a3d] text-slate-200 rounded-tl-sm"}`} data-testid={`chatbot-msg-${i}`}>
                {m.text || <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
            ))}
            {handoff && (
              <form onSubmit={talkToSupport} className="space-y-2 bg-[#081430] p-3 rounded-xl border border-[#007bff]/30" data-testid="handoff-form">
                <p className="text-slate-300">Leave your details and our team will get back to you.</p>
                <Input required placeholder="Name" value={hf.name} onChange={(e) => setHf({ ...hf, name: e.target.value })} data-testid="handoff-name" />
                <Input required type="email" placeholder="Email" value={hf.email} onChange={(e) => setHf({ ...hf, email: e.target.value })} data-testid="handoff-email" />
                <Input placeholder="How can we help?" value={hf.message} onChange={(e) => setHf({ ...hf, message: e.target.value })} data-testid="handoff-message" />
                <button className="btn-electric w-full !py-2" data-testid="handoff-submit">Send to support</button>
              </form>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-3 border-t border-[#007bff]/30 space-y-2">
            <button onClick={() => (user && user.role !== "admin" ? talkToSupport() : setHandoff(true))} className="w-full flex items-center justify-center gap-2 text-[#00d9ff] font-cond font-bold uppercase tracking-wider text-sm py-1.5 rounded-lg border border-[#00d9ff]/30 hover:bg-[#007bff]/10" data-testid="talk-to-support-btn">
              <Headset className="w-4 h-4" /> Talk to Support
            </button>
            <form onSubmit={send} className="flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask a question..." className="bg-[#040a1a]" data-testid="chatbot-input" />
              <button disabled={busy} className="btn-electric !px-3 !py-2" data-testid="chatbot-send-btn"><Send className="w-4 h-4" /></button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
